/**
 * Bakery pickup — order-ahead calendar
 * Reston Farm Garden Market
 *
 * The problem this solves: the bakery picker only shows the next ten days as
 * tiles, so an office ordering donuts for a date three weeks out had no way to
 * do it online and had to email the market.
 *
 * What it does: adds a "More dates" button that opens a month calendar. Any
 * open pickup day up to N days out can be chosen. Closed days (Thanksgiving,
 * Christmas) are blocked here and are also left out of the tile strip by the
 * snippet.
 *
 * Contract with product-adder.js (that file is unchanged):
 *   - product-adder.js binds a click handler to every .adder-date-tile at init,
 *     including the hidden [data-cal-custom] tile the snippet renders first in
 *     the strip.
 *   - Picking a date already in the strip clicks that tile.
 *   - Picking a later date rewrites the custom tile's data-* and labels,
 *     un-hides it and clicks it. Selection, the Pickup Date / Pickup Time
 *     line-item properties and the add-to-cart lock all run through the engine
 *     exactly as they do for a normal tile.
 *   - product-adder-lead-time.js still owns "too soon" for today. This file
 *     only uses the same clock (data-shop-utc-offset) to decide whether today
 *     can still be offered in the calendar.
 */
(function () {
  'use strict';

  var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var MON_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'];
  var DAY_MS = 86400000;
  var ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

  var SVG_OPEN = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">';
  var ICON_PREV = SVG_OPEN + '<path d="M10 3.5 5.5 8l4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_NEXT = SVG_OPEN + '<path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_CLOSE = SVG_OPEN + '<path d="M4.5 4.5l7 7M11.5 4.5l-7 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
  var ICON_FLAG = SVG_OPEN + '<rect x="2" y="3" width="12" height="11" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  /* ── Date helpers. Every date is a UTC-midnight Date, so DST never shifts a day. ── */

  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function toIso(d) { return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate()); }
  function fromIso(s) {
    var p = String(s).split('-');
    return new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]));
  }
  function addDays(d, n) { return new Date(d.getTime() + n * DAY_MS); }
  function monthStart(d) { return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)); }
  function addMonths(d, n) { return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1)); }
  function daysInMonth(d) { return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate(); }
  function sameMonth(a, b) { return a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth(); }

  /** Same format the snippet uses for data-date-display: '%a, %b %-d, %Y'. */
  function lineItemDate(d) {
    return DOW[d.getUTCDay()] + ', ' + MON[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
  }
  function longDate(d) { return DOW_FULL[d.getUTCDay()] + ', ' + MON_FULL[d.getUTCMonth()] + ' ' + d.getUTCDate(); }
  function shortDate(d) { return DOW[d.getUTCDay()] + ', ' + MON[d.getUTCMonth()] + ' ' + d.getUTCDate(); }

  /** "10am–11am" -> 600. Mirrors product-adder-lead-time.js. */
  function startMinutes(label) {
    if (!label) return null;
    var head = String(label).split(/[–—-]/)[0];
    var parts = head.match(/(\d{1,2})(?::(\d{2}))?\s*([ap])m/i);
    if (!parts) return null;
    var hour = parseInt(parts[1], 10) % 12;
    if (/p/i.test(parts[3])) hour += 12;
    return hour * 60 + (parts[2] ? parseInt(parts[2], 10) : 0);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ════════════════════════════════════════════════════════════ */

  function init() {
    var root = document.querySelector('.product-adder[data-adder-type="option_schedule"]');
    if (!root) return;
    var panel = root.querySelector('[data-cal-panel]');
    var trigger = root.querySelector('[data-cal-trigger]');
    var strip = root.querySelector('.adder-date-tiles');
    var custom = root.querySelector('[data-cal-custom]');
    if (!panel || !trigger || !strip || !custom) return;

    var timeSection = root.querySelector('[data-section="times"]');

    /* ── Config ── */
    var pickupDays = {};
    String(panel.dataset.pickupDays || '').split(',').forEach(function (d) {
      var key = d.trim().slice(0, 3);
      if (key) pickupDays[key] = true;
    });
    var anyPickupDay = Object.keys(pickupDays).length > 0;

    var slotStarts = String(panel.dataset.pickupTimes || '').split(',')
      .map(function (t) { return startMinutes(t.trim()); })
      .filter(function (m) { return m !== null; });

    var maxDays = parseInt(panel.dataset.maxDays, 10);
    if (isNaN(maxDays) || maxDays < 1) maxDays = 56;

    var leadMinutes = timeSection ? parseInt(timeSection.dataset.leadMinutes, 10) : NaN;
    if (isNaN(leadMinutes) || leadMinutes < 0) leadMinutes = 60;

    var shopOffset = timeSection ? parseInt(timeSection.dataset.shopUtcOffset, 10) : NaN;

    var closed = {};
    String(panel.dataset.closed || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split('|');
      var iso = (parts[0] || '').trim();
      if (!ISO_RE.test(iso)) return;
      closed[iso] = ((parts[1] || '').trim()) || 'Closed';
    });

    var note = panel.dataset.note || '';
    var eyebrow = panel.dataset.eyebrow || 'Order ahead';

    /* ── State ── */
    var view = null;        /* first of the month on screen */
    var selected = null;    /* iso currently chosen in the picker */
    var focusIso = null;    /* roving tabindex target */
    var isOpen = false;
    var closeTimer = null;

    /* ── Clock ── */

    function shopNow() {
      if (!isNaN(shopOffset)) {
        var shifted = new Date(Date.now() + shopOffset * 60000);
        return { iso: shifted.toISOString().slice(0, 10), minutes: shifted.getUTCHours() * 60 + shifted.getUTCMinutes() };
      }
      var local = new Date();
      return {
        iso: local.getFullYear() + '-' + pad(local.getMonth() + 1) + '-' + pad(local.getDate()),
        minutes: local.getHours() * 60 + local.getMinutes()
      };
    }

    function context() {
      var now = shopNow();
      var today = fromIso(now.iso);
      var cutoff = now.minutes + leadMinutes;
      var todayOpen = slotStarts.length === 0 || slotStarts.some(function (m) { return m >= cutoff; });
      return { today: today, todayIso: now.iso, todayOpen: todayOpen, maxDate: addDays(today, maxDays) };
    }

    function status(d, ctx) {
      var iso = toIso(d);
      if (d < ctx.today) return { ok: false, reason: 'Past date', kind: 'past' };
      if (d > ctx.maxDate) return { ok: false, reason: 'Opens for orders closer to the day', kind: 'later' };
      if (closed[iso]) return { ok: false, reason: closed[iso], kind: 'closed' };
      if (anyPickupDay && !pickupDays[DOW[d.getUTCDay()]]) return { ok: false, reason: 'No bakery pickups this day', kind: 'off' };
      if (iso === ctx.todayIso && !ctx.todayOpen) return { ok: false, reason: 'Too late for today', kind: 'past' };
      return { ok: true, reason: 'Open for pickup', kind: 'open' };
    }

    function firstOpenFrom(start, ctx, limitDays) {
      var d = start < ctx.today ? ctx.today : start;
      for (var i = 0; i <= (limitDays || 60); i++) {
        if (d > ctx.maxDate) return null;
        if (status(d, ctx).ok) return d;
        d = addDays(d, 1);
      }
      return null;
    }

    /* ── Quick picks ── */

    function chips(ctx) {
      var t = ctx.today;
      var dow = t.getUTCDay();
      var out = [];
      var seen = {};

      function push(label, d) {
        if (!d) return;
        var iso = toIso(d);
        if (seen[iso]) return;
        seen[iso] = true;
        out.push({ label: label, iso: iso, d: d });
      }

      /* This weekend: today if it's Sat/Sun, else the coming Saturday. Two days to look. */
      var sat = dow === 0 ? t : addDays(t, (6 - dow + 7) % 7);
      var weekend = firstOpenFrom(sat, ctx, dow === 0 ? 0 : 1);
      push('This weekend', weekend);

      /* Next week: the first open day from next Monday. */
      var nextMon = addDays(t, ((8 - dow) % 7) || 7);
      push('Next week', firstOpenFrom(nextMon, ctx, 6));

      /* Two weeks out. */
      push('In 2 weeks', firstOpenFrom(addDays(t, 14), ctx, 6));

      return out;
    }

    /* ── Render ── */

    function render(direction) {
      var ctx = context();
      if (!view) view = monthStart(ctx.today);

      var minView = monthStart(ctx.today);
      var maxView = monthStart(ctx.maxDate);
      if (view < minView) view = minView;
      if (view > maxView) view = maxView;

      if (!focusIso || !sameMonth(fromIso(focusIso), view)) {
        var seed = selected && sameMonth(fromIso(selected), view) ? fromIso(selected) : null;
        if (!seed) seed = firstOpenFrom(view, ctx, 31);
        if (!seed || !sameMonth(seed, view)) seed = sameMonth(view, ctx.today) ? ctx.today : view;
        focusIso = toIso(seed);
      }

      var h = [];

      /* Intro */
      h.push('<div class="rfcal__top">');
      h.push('<div class="rfcal__intro">');
      h.push('<p class="rfcal__eyebrow">' + escapeHtml(eyebrow) + '</p>');
      if (note) h.push('<p class="rfcal__note">' + escapeHtml(note) + '</p>');
      h.push('</div>');
      h.push('<button type="button" class="rfcal__close" data-cal-close aria-label="Close calendar">' + ICON_CLOSE + '</button>');
      h.push('</div>');

      /* Quick picks */
      var quick = chips(ctx);
      if (quick.length) {
        h.push('<div class="rfcal__chips" role="group" aria-label="Quick picks">');
        quick.forEach(function (c) {
          var active = c.iso === selected;
          h.push('<button type="button" class="rfcal__chip' + (active ? ' is-active' : '') + '" data-cal-chip="' + c.iso + '" aria-pressed="' + active + '">' +
            '<span>' + escapeHtml(c.label) + '</span><small>' + escapeHtml(shortDate(c.d)) + '</small></button>');
        });
        h.push('</div>');
      }

      /* Month header */
      var prevOk = view > minView;
      var nextOk = view < maxView;
      h.push('<div class="rfcal__head">');
      h.push('<button type="button" class="rfcal__nav" data-cal-nav="-1" aria-label="Previous month"' + (prevOk ? '' : ' disabled') + '>' + ICON_PREV + '</button>');
      h.push('<h3 class="rfcal__title" id="' + panel.id + '-title" aria-live="polite">' + MON_FULL[view.getUTCMonth()] + ' <em>' + view.getUTCFullYear() + '</em></h3>');
      h.push('<button type="button" class="rfcal__nav" data-cal-nav="1" aria-label="Next month"' + (nextOk ? '' : ' disabled') + '>' + ICON_NEXT + '</button>');
      h.push('</div>');

      /* Grid: only as many week rows as the month needs (4–6). */
      var rows = Math.ceil((view.getUTCDay() + daysInMonth(view)) / 7);
      var gridClass = 'rfcal__grid' + (direction && !reducedMotion ? (direction > 0 ? ' rfcal__grid--next' : ' rfcal__grid--prev') : '');
      h.push('<div class="' + gridClass + '" role="grid" aria-labelledby="' + panel.id + '-title">');
      h.push('<div class="rfcal__row rfcal__row--dow" role="row">');
      for (var w = 0; w < 7; w++) {
        h.push('<div class="rfcal__dow" role="columnheader"><abbr title="' + DOW_FULL[w] + '">' + DOW[w].charAt(0) + '<span class="rfcal__dow-rest">' + DOW[w].slice(1) + '</span></abbr></div>');
      }
      h.push('</div>');

      var cursor = addDays(view, -view.getUTCDay());
      for (var r = 0; r < rows; r++) {
        h.push('<div class="rfcal__row" role="row">');
        for (var c = 0; c < 7; c++) {
          if (!sameMonth(cursor, view)) {
            h.push('<div class="rfcal__cell rfcal__cell--blank" role="gridcell"></div>');
          } else {
            var iso = toIso(cursor);
            var st = status(cursor, ctx);
            var cls = 'rfcal__day';
            if (iso === ctx.todayIso) cls += ' rfcal__day--today';
            if (st.kind === 'closed') cls += ' rfcal__day--closed';
            if (st.kind === 'later') cls += ' rfcal__day--later';
            if (iso === selected) cls += ' rfcal__day--selected';
            var label = longDate(cursor) + ', ' + cursor.getUTCFullYear() + '. ' + st.reason + (iso === ctx.todayIso ? '. Today' : '');
            h.push('<div class="rfcal__cell" role="gridcell" aria-selected="' + (iso === selected) + '">' +
              '<button type="button" class="' + cls + '" data-cal-day="' + iso + '"' +
              ' tabindex="' + (iso === focusIso ? '0' : '-1') + '"' +
              (st.ok ? '' : ' aria-disabled="true"') +
              (iso === ctx.todayIso ? ' aria-current="date"' : '') +
              ' aria-label="' + escapeHtml(label) + '"' +
              (st.ok ? '' : ' data-reason="' + escapeHtml(st.reason) + '"') + '>' +
              '<span class="rfcal__num">' + cursor.getUTCDate() + '</span>' +
              '</button></div>');
          }
          cursor = addDays(cursor, 1);
        }
        h.push('</div>');
      }
      h.push('</div>');

      /* Footer */
      h.push('<div class="rfcal__foot">');
      h.push('<div class="rfcal__legend" aria-hidden="true">' +
        '<span class="rfcal__key rfcal__key--today">Today</span>' +
        (Object.keys(closed).length ? '<span class="rfcal__key rfcal__key--closed">Closed</span>' : '') +
        '</div>');
      h.push('<p class="rfcal__status" data-cal-status aria-live="polite">' + escapeHtml(restingStatus(ctx)) + '</p>');
      h.push('</div>');

      panel.innerHTML = h.join('');
    }

    function restingStatus(ctx) {
      if (selected) return 'Pickup ' + longDate(fromIso(selected));
      return 'Taking orders through ' + shortDate(ctx.maxDate);
    }

    function setStatus(text) {
      var el = panel.querySelector('[data-cal-status]');
      if (el) el.textContent = text;
    }

    function focusDay(iso) {
      var btn = panel.querySelector('[data-cal-day="' + iso + '"]');
      if (!btn) return;
      panel.querySelectorAll('[data-cal-day]').forEach(function (b) { b.tabIndex = -1; });
      btn.tabIndex = 0;
      btn.focus({ preventScroll: true });
    }

    /* ── Open / close ── */

    function open() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      var ctx = context();
      var anchor = selected && fromIso(selected) >= ctx.today ? fromIso(selected) : ctx.today;
      view = monthStart(anchor);
      focusIso = null;
      isOpen = true;
      render(0);
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      if (!reducedMotion) {
        panel.classList.remove('is-entering');
        void panel.offsetWidth;
        panel.classList.add('is-entering');
      }
      focusDay(focusIso);
      var rect = panel.getBoundingClientRect();
      if (rect.bottom > window.innerHeight) {
        panel.scrollIntoView({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
      }
    }

    function close(returnFocus) {
      if (!isOpen) return;
      isOpen = false;
      panel.hidden = true;
      panel.classList.remove('is-entering');
      trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger.focus();
    }

    /* ── Choosing a date ── */

    function scrollTileIntoView(tile) {
      var s = strip.getBoundingClientRect();
      var t = tile.getBoundingClientRect();
      if (t.left >= s.left && t.right <= s.right) return;
      var left = strip.scrollLeft + (t.left - s.left) - 8;
      if (strip.scrollTo) strip.scrollTo({ left: Math.max(0, left), behavior: reducedMotion ? 'auto' : 'smooth' });
      else strip.scrollLeft = Math.max(0, left);
    }

    function fillCustom(d) {
      custom.dataset.date = toIso(d);
      custom.dataset.dayName = DOW_FULL[d.getUTCDay()];
      custom.dataset.dateDisplay = lineItemDate(d);
      custom.setAttribute('aria-label', 'Pickup ' + DOW_FULL[d.getUTCDay()] + ', ' + MON[d.getUTCMonth()] + ' ' + d.getUTCDate() + ' (ordered ahead)');
      var day = custom.querySelector('[data-cal-custom-day]');
      var num = custom.querySelector('[data-cal-custom-num]');
      var mon = custom.querySelector('[data-cal-custom-month]');
      if (day) day.textContent = DOW[d.getUTCDay()];
      if (num) num.textContent = d.getUTCDate();
      if (mon) mon.textContent = MON[d.getUTCMonth()];
      var flag = custom.querySelector('.adder-date-tile__flag');
      if (flag && !flag.innerHTML) flag.innerHTML = ICON_FLAG;
    }

    function choose(iso) {
      var d = fromIso(iso);
      var match = strip.querySelector('.adder-date-tile[data-date="' + iso + '"]:not([data-cal-custom])');
      var target;

      if (match && !match.disabled) {
        custom.hidden = true;
        target = match;
      } else {
        fillCustom(d);
        custom.hidden = false;
        custom.disabled = false;
        custom.classList.remove('adder-date-tile--past');
        target = custom;
        if (!reducedMotion) {
          custom.classList.remove('is-arriving');
          void custom.offsetWidth;
          custom.classList.add('is-arriving');
        }
      }

      /* Runs product-adder.js's own handler: selection, properties, ATC lock. */
      target.click();
      selected = iso;
      focusIso = iso;
      scrollTileIntoView(target);

      render(0);
      focusDay(iso);
      setStatus('Pickup ' + longDate(d) + '. Now choose a time.');

      closeTimer = setTimeout(function () {
        closeTimer = null;
        close(false);
        target.focus({ preventScroll: true });
      }, reducedMotion ? 0 : 420);
    }

    function shiftMonth(delta) {
      view = addMonths(view, delta);
      focusIso = null;
      render(delta);
      /* The re-render replaced the button that had focus; put focus back on
         the same arrow, or on the grid when that arrow is now disabled. */
      var again = panel.querySelector('[data-cal-nav="' + delta + '"]');
      if (again && !again.disabled) again.focus({ preventScroll: true });
      else focusDay(focusIso);
    }

    /* ── Events ── */

    trigger.addEventListener('click', function () {
      if (isOpen) close(false); else open();
    });

    panel.addEventListener('click', function (e) {
      var day = e.target.closest('[data-cal-day]');
      if (day) {
        if (day.getAttribute('aria-disabled') === 'true') {
          setStatus(shortDate(fromIso(day.dataset.calDay)) + ' · ' + (day.dataset.reason || 'Unavailable'));
          day.classList.remove('is-nope');
          void day.offsetWidth;
          if (!reducedMotion) day.classList.add('is-nope');
          return;
        }
        choose(day.dataset.calDay);
        return;
      }
      var chip = e.target.closest('[data-cal-chip]');
      if (chip) {
        var d = fromIso(chip.dataset.calChip);
        if (!sameMonth(d, view)) view = monthStart(d);
        choose(chip.dataset.calChip);
        return;
      }
      var nav = e.target.closest('[data-cal-nav]');
      if (nav && !nav.disabled) {
        shiftMonth(parseInt(nav.dataset.calNav, 10));
        return;
      }
      if (e.target.closest('[data-cal-close]')) close(true);
    });

    panel.addEventListener('mouseover', function (e) {
      var day = e.target.closest('[data-cal-day]');
      if (!day) return;
      var d = fromIso(day.dataset.calDay);
      setStatus(shortDate(d) + ' · ' + (day.dataset.reason || 'Open for pickup'));
    });

    panel.addEventListener('mouseleave', function () {
      if (isOpen) setStatus(restingStatus(context()));
    });

    panel.addEventListener('focusin', function (e) {
      var day = e.target.closest('[data-cal-day]');
      if (!day) return;
      focusIso = day.dataset.calDay;
    });

    panel.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
        return;
      }
      var day = e.target.closest('[data-cal-day]');
      if (!day) return;

      var d = fromIso(day.dataset.calDay);
      var nd = null;
      switch (e.key) {
        case 'ArrowLeft': nd = addDays(d, -1); break;
        case 'ArrowRight': nd = addDays(d, 1); break;
        case 'ArrowUp': nd = addDays(d, -7); break;
        case 'ArrowDown': nd = addDays(d, 7); break;
        case 'Home': nd = addDays(d, -d.getUTCDay()); break;
        case 'End': nd = addDays(d, 6 - d.getUTCDay()); break;
        case 'PageUp':
        case 'PageDown': {
          var m = addMonths(monthStart(d), e.key === 'PageUp' ? -1 : 1);
          nd = new Date(Date.UTC(m.getUTCFullYear(), m.getUTCMonth(), Math.min(d.getUTCDate(), daysInMonth(m))));
          break;
        }
        default: return;
      }
      e.preventDefault();

      var ctx = context();
      var lo = monthStart(ctx.today);
      var hi = addDays(addMonths(monthStart(ctx.maxDate), 1), -1);
      if (nd < lo) nd = lo;
      if (nd > hi) nd = hi;

      var ndIso = toIso(nd);
      if (!sameMonth(nd, view)) {
        var dir = nd > view ? 1 : -1;
        view = monthStart(nd);
        focusIso = ndIso;
        render(dir);
      } else {
        focusIso = ndIso;
      }
      focusDay(ndIso);
    });

    /* Keep the calendar in step when a tile in the strip is clicked directly. */
    strip.addEventListener('click', function (e) {
      var tile = e.target.closest('.adder-date-tile');
      if (!tile || tile.disabled) return;
      selected = tile.dataset.date;
      if (tile !== custom && !custom.hidden) {
        /* Leave the ordered-ahead tile in place so it can be re-chosen. */
        custom.setAttribute('aria-selected', 'false');
      }
      if (isOpen) render(0);
    });

    /* Remove a stale custom date (page left open past the date). */
    setInterval(function () {
      if (custom.hidden) return;
      var ctx = context();
      var d = fromIso(custom.dataset.date);
      if (d < ctx.today) custom.hidden = true;
    }, 60000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
